/**
 * Session Storage Utilities for SSO/OIDC Flow
 *
 * This module provides centralized utilities for managing session storage
 * during the SSO (Single Sign-On) and OIDC (OpenID Connect) authentication flow.
 *
 * It eliminates duplication of session storage key strings and provides
 * type-safe helper functions for storing and retrieving OIDC session data.
 */

// Session storage key constants
const OIDC_STATE_KEY = 'oidc_state'
const OIDC_CODE_VERIFIER_KEY = 'oidc_code_verifier'
const OIDC_NONCE_KEY = 'oidc_nonce'
const OIDC_REDIRECT_URI_KEY = 'oidc_redirect_uri'
const SSO_LINKING_TENANT_KEY = 'sso_linking_tenant_key'

/**
 * Stores OIDC session parameters in sessionStorage
 *
 * This function is called before initiating the OAuth/OIDC authorization flow.
 * It stores the state, code verifier, nonce, and redirect URI that will be
 * needed when processing the callback from the Identity Provider.
 *
 * @param {Object} params - OIDC session parameters
 * @param {string} params.state - CSRF protection state parameter
 * @param {string} params.codeVerifier - PKCE code verifier for token exchange
 * @param {string} params.nonce - Nonce for ID token validation
 * @param {string} params.redirectUri - Redirect URI registered with the IdP
 * @returns {void}
 *
 * @example
 * storeOidcSession({
 *   state: 'abc123',
 *   codeVerifier: 'xyz789',
 *   nonce: 'nonce456',
 *   redirectUri: 'http://localhost:8102/sso-callback'
 * })
 */
export function storeOidcSession({ state, codeVerifier, nonce, redirectUri }) {
  sessionStorage.setItem(OIDC_STATE_KEY, state)
  sessionStorage.setItem(OIDC_CODE_VERIFIER_KEY, codeVerifier)
  sessionStorage.setItem(OIDC_NONCE_KEY, nonce)
  sessionStorage.setItem(OIDC_REDIRECT_URI_KEY, redirectUri)
}

/**
 * Retrieves OIDC session parameters from sessionStorage
 *
 * This function is called in the OAuth/OIDC callback handler to retrieve
 * the stored session parameters needed to complete the authentication flow.
 *
 * @returns {Object|null} OIDC session parameters or null if any required parameter is missing
 * @returns {string} return.state - CSRF protection state parameter
 * @returns {string} return.codeVerifier - PKCE code verifier for token exchange
 * @returns {string} return.nonce - Nonce for ID token validation
 * @returns {string} return.redirectUri - Redirect URI registered with the IdP
 *
 * @example
 * const oidcSession = getOidcSession()
 * if (!oidcSession) {
 *   console.error('OIDC session expired')
 *   return
 * }
 * // Use oidcSession.state, oidcSession.codeVerifier, etc.
 */
export function getOidcSession() {
  const state = sessionStorage.getItem(OIDC_STATE_KEY)
  const codeVerifier = sessionStorage.getItem(OIDC_CODE_VERIFIER_KEY)
  const nonce = sessionStorage.getItem(OIDC_NONCE_KEY)
  const redirectUri = sessionStorage.getItem(OIDC_REDIRECT_URI_KEY)

  // Return null if any required parameter is missing
  if (!state || !codeVerifier || !nonce || !redirectUri) {
    return null
  }

  return {
    state,
    codeVerifier,
    nonce,
    redirectUri,
  }
}

/**
 * Clears all OIDC session parameters from sessionStorage
 *
 * This function should be called after successfully completing the OAuth/OIDC
 * callback flow (either login or account linking) to clean up session data.
 *
 * @returns {void}
 *
 * @example
 * // After successful login
 * clearOidcSession()
 */
export function clearOidcSession() {
  sessionStorage.removeItem(OIDC_STATE_KEY)
  sessionStorage.removeItem(OIDC_CODE_VERIFIER_KEY)
  sessionStorage.removeItem(OIDC_NONCE_KEY)
  sessionStorage.removeItem(OIDC_REDIRECT_URI_KEY)
}

/**
 * Stores the tenant key for SSO account linking flow
 *
 * This function is called when a user initiates the SSO account linking process
 * from the settings dropdown. The stored tenant key allows the callback handler
 * to distinguish between a new login flow and an account linking flow.
 *
 * @param {string} tenantKey - The SSO tenant key for the practice
 * @returns {void}
 *
 * @example
 * // User clicks "Link to SSO" in settings
 * setAccountLinkingContext('tenant-abc-123')
 */
export function setAccountLinkingContext(tenantKey) {
  sessionStorage.setItem(SSO_LINKING_TENANT_KEY, tenantKey)
}

/**
 * Retrieves the stored tenant key for SSO account linking flow
 *
 * This function is used to check if the current flow is an account linking
 * operation (as opposed to a new login).
 *
 * @returns {string|null} The stored tenant key, or null if not in linking flow
 *
 * @example
 * const linkingTenantKey = getAccountLinkingContext()
 * if (linkingTenantKey) {
 *   console.log('This is an account linking flow')
 * }
 */
export function getAccountLinkingContext() {
  return sessionStorage.getItem(SSO_LINKING_TENANT_KEY)
}

/**
 * Checks if the current flow is an SSO account linking operation
 *
 * Convenience function to determine if the user is in the middle of
 * linking their existing account to an SSO identity.
 *
 * @returns {boolean} True if in account linking flow, false otherwise
 *
 * @example
 * if (isAccountLinking()) {
 *   showMessage('Linking your account to SSO...')
 * }
 */
export function isAccountLinking() {
  return sessionStorage.getItem(SSO_LINKING_TENANT_KEY) !== null
}

/**
 * Clears the SSO account linking context from sessionStorage
 *
 * This function should be called after the account linking process completes
 * (either successfully or with an error) to clean up the linking context.
 *
 * @returns {void}
 *
 * @example
 * // After successful account linking
 * clearAccountLinkingContext()
 */
export function clearAccountLinkingContext() {
  sessionStorage.removeItem(SSO_LINKING_TENANT_KEY)
}

/**
 * Clears all SSO-related session storage data
 *
 * This is a convenience function that clears both OIDC session data and
 * account linking context. Useful for cleanup when navigating away from
 * SSO flows or handling errors.
 *
 * @returns {void}
 *
 * @example
 * // On SSO error or user cancellation
 * clearAllSsoSession()
 */
export function clearAllSsoSession() {
  clearOidcSession()
  clearAccountLinkingContext()
}
